import { FC, memo, ReactNode, useState, useRef, MouseEvent } from 'react';
import {
  InputAdornment,
  IconButton,
  PopperPlacementType,
} from '@material-ui/core';
import { Clear } from '@material-ui/icons';

import {
  Template as UITemplate,
  Field,
  NumberOfActiveFilters,
} from './template.styles';
import { Popper } from './popper/popper';
import { FilterOnClear } from '../filters.types';
import { OnClose } from './template.types';
import { display } from '@material-ui/system';

const DEFAULT_PLACEMENT = 'bottom-start';

type Props = {
  children: React.ReactNode;
  isFilled: boolean;
  title: string;
  inputValue: string;
  icon: ReactNode;
  numberOfActiveFilters?: number;
  isDisabled?: boolean;
  titleHelper?: ReactNode;
  className?: string;
  placement?: PopperPlacementType;
  onClear?: FilterOnClear;
};

type OnClear = (
  e: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>
) => void;

type ExpectedGenerateInputProps = {
  readOnly: boolean;
  startAdornment: JSX.Element;
  endAdornment?: JSX.Element;
};

type GenerateInputPropsArgs = {
  icon: ReactNode;
  isVisibleClearButton: boolean;
  numberOfActiveFilters?: number;
  onClear: OnClear;
};

type GenerateInputProps = (
  args: GenerateInputPropsArgs
) => ExpectedGenerateInputProps;

const generateInputProps: GenerateInputProps = ({
  icon,
  isVisibleClearButton,
  numberOfActiveFilters,
  onClear,
}) => {
  const defaultInputProps = {
    startAdornment: <InputAdornment position="start">{icon}</InputAdornment>,
    readOnly: true,
  };

  if (numberOfActiveFilters && numberOfActiveFilters !== 0) {
    return {
      ...defaultInputProps,
      endAdornment: (
        <InputAdornment position="end">
          <NumberOfActiveFilters>{numberOfActiveFilters}</NumberOfActiveFilters>
        </InputAdornment>
      ),
    };
  }

  if (isVisibleClearButton) {
    return {
      ...defaultInputProps,
      endAdornment: (
        <InputAdornment position="start">
          <IconButton size="small" onClick={onClear}>
            {/* commented out as I'm sure we'll probably need this. */}
            {/* <Clear /> */}
          </IconButton>
        </InputAdornment>
      ),
    };
  }

  return defaultInputProps;
};

export const Template: FC<Props> = memo(
  ({
    isFilled,
    isDisabled = false,
    title,
    titleHelper,
    inputValue,
    icon,
    children,
    className,
    placement = DEFAULT_PLACEMENT,
    numberOfActiveFilters,
    onClear,
  }) => {
    const [open, setOpen] = useState<boolean>(false);
    const anchorRef = useRef<HTMLDivElement>(null);
    const isVisibleClearButton = !!onClear;

    const handleToggle = () => {
      if (!isDisabled) {
        setOpen((prevOpen) => !prevOpen);
      }
    };

    const handleClose: OnClose = ({ target, type }) => {
      if (target.nodeName === 'BODY' && type === 'click') {
        return;
      }

      setOpen(false);
    };

    const handleClear: OnClear = (e) => {
      e.stopPropagation();

      if (onClear) {
        onClear();
      }
    };

    return (
      <UITemplate
        $isFocused={open}
        $isFilled={isFilled}
        $isDisabled={isDisabled}
        className={className}
      >
        <Field
          formControlRef={anchorRef}
          aria-controls={open ? 'popper-open' : undefined}
          aria-haspopup="true"
          value={inputValue}
          InputProps={generateInputProps({
            icon,
            isVisibleClearButton,
            numberOfActiveFilters,
            onClear: handleClear,
          })}
          onClick={handleToggle}
        />

        {!isDisabled && (
          <Popper
            title={title}
            titleHelper={titleHelper}
            open={open}
            anchorEl={anchorRef.current}
            placement={placement}
            onClose={handleClose}
          >
            {children}
          </Popper>
        )}
      </UITemplate>
    );
  }
);
